import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ProfileSection,
  createProfileSection,
  createProfileSectionWithImage,
  deleteProfileSection,
  getProfileSections,
  reorderProfileSections,
  updateProfileSection,
  updateProfileSectionImage,
  removeProfileSectionImage,
} from "@/lib/api/profile-sections";

export const useProfileSections = (userId: string) => {
  return useQuery({
    queryKey: ["profile-sections", userId],
    queryFn: () => getProfileSections(userId),
    enabled: !!userId,
  });
};

export const useCreateProfileSection = (userId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      title,
      description,
      metadata = {},
    }: {
      title: string;
      description: string;
      metadata?: Record<string, unknown>;
    }) => createProfileSection(userId, title, description, metadata),
    onMutate: async (newSection) => {
      await queryClient.cancelQueries({
        queryKey: ["profile-sections", userId],
      });

      const previousSections = queryClient.getQueryData<ProfileSection[]>([
        "profile-sections",
        userId,
      ]);

      const optimisticSection: ProfileSection = {
        id: `temp-${Date.now()}`,
        title: newSection.title,
        description: newSection.description,
        position: previousSections?.length || 0,
        metadata: newSection.metadata || {},
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      queryClient.setQueryData<ProfileSection[]>(
        ["profile-sections", userId],
        (old) => [...(old || []), optimisticSection],
      );

      return { previousSections };
    },
    onError: (err, _, context) => {
      queryClient.setQueryData(
        ["profile-sections", userId],
        context?.previousSections,
      );
      console.error("Failed to create profile section:", err);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["profile-sections", userId] });
    },
  });
};

export const useCreateProfileSectionWithImage = (userId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      title,
      description,
      file,
      metadata = {},
    }: {
      title: string;
      description: string;
      file: File;
      metadata?: Record<string, unknown>;
    }) =>
      createProfileSectionWithImage(userId, title, description, file, metadata),
    onError: (err) => {
      console.error("Failed to create profile section with image:", err);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["profile-sections", userId] });
    },
  });
};

export const useUpdateProfileSection = (userId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sectionId,
      title,
      description,
      metadata,
    }: {
      sectionId: string;
      title?: string;
      description?: string;
      metadata?: Record<string, unknown>;
    }) => updateProfileSection(sectionId, title, description, metadata),
    onMutate: async (updatedSection) => {
      await queryClient.cancelQueries({
        queryKey: ["profile-sections", userId],
      });

      const previousSections = queryClient.getQueryData<ProfileSection[]>([
        "profile-sections",
        userId,
      ]);

      queryClient.setQueryData<ProfileSection[]>(
        ["profile-sections", userId],
        (old) =>
          old?.map((section) =>
            section.id === updatedSection.sectionId
              ? {
                  ...section,
                  ...(updatedSection.title && { title: updatedSection.title }),
                  ...(updatedSection.description && {
                    description: updatedSection.description,
                  }),
                  ...(updatedSection.metadata && {
                    metadata: updatedSection.metadata,
                  }),
                  updated_at: new Date().toISOString(),
                }
              : section,
          ) || [],
      );

      return { previousSections };
    },
    onError: (err, _, context) => {
      queryClient.setQueryData(
        ["profile-sections", userId],
        context?.previousSections,
      );
      console.error("Failed to update profile section:", err);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["profile-sections", userId] });
    },
  });
};

export const useUpdateProfileSectionImage = (userId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sectionId, file }: { sectionId: string; file: File }) =>
      updateProfileSectionImage(sectionId, file),
    onError: (err) => {
      console.error("Failed to update profile section image:", err);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["profile-sections", userId] });
    },
  });
};

export const useDeleteProfileSection = (userId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sectionId: string) => deleteProfileSection(sectionId),
    onMutate: async (sectionId) => {
      await queryClient.cancelQueries({
        queryKey: ["profile-sections", userId],
      });

      const previousSections = queryClient.getQueryData<ProfileSection[]>([
        "profile-sections",
        userId,
      ]);

      queryClient.setQueryData<ProfileSection[]>(
        ["profile-sections", userId],
        (old) => old?.filter((section) => section.id !== sectionId) || [],
      );

      return { previousSections };
    },
    onError: (err, _, context) => {
      queryClient.setQueryData(
        ["profile-sections", userId],
        context?.previousSections,
      );
      console.error("Failed to delete profile section:", err);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["profile-sections", userId] });
    },
  });
};

export const useReorderProfileSections = (userId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sectionIds: string[]) =>
      reorderProfileSections(userId, sectionIds),
    onMutate: async (sectionIds) => {
      await queryClient.cancelQueries({
        queryKey: ["profile-sections", userId],
      });

      const previousSections = queryClient.getQueryData<ProfileSection[]>([
        "profile-sections",
        userId,
      ]);

      if (previousSections) {
        const reorderedSections = sectionIds
          .map((id, index) => {
            const section = previousSections.find((s) => s.id === id);
            return section ? { ...section, position: index } : null;
          })
          .filter((section): section is ProfileSection => section !== null);

        queryClient.setQueryData<ProfileSection[]>(
          ["profile-sections", userId],
          reorderedSections,
        );
      }

      return { previousSections };
    },
    onError: (err, _, context) => {
      queryClient.setQueryData(
        ["profile-sections", userId],
        context?.previousSections,
      );
      console.error("Failed to reorder profile sections:", err);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["profile-sections", userId] });
    },
  });
};

export const useRemoveProfileSectionImage = (userId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sectionId: string) => removeProfileSectionImage(sectionId),
    onMutate: async (sectionId) => {
      await queryClient.cancelQueries({
        queryKey: ["profile-sections", userId],
      });

      const previousSections = queryClient.getQueryData<ProfileSection[]>([
        "profile-sections",
        userId,
      ]);

      queryClient.setQueryData<ProfileSection[]>(
        ["profile-sections", userId],
        (old) =>
          old?.map((section) =>
            section.id === sectionId
              ? {
                  ...section,
                  image_url: undefined,
                  updated_at: new Date().toISOString(),
                }
              : section,
          ) || [],
      );

      return { previousSections };
    },
    onError: (err, _, context) => {
      queryClient.setQueryData(
        ["profile-sections", userId],
        context?.previousSections,
      );
      console.error("Failed to remove profile section image:", err);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["profile-sections", userId] });
    },
  });
};
